import React from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import {
    Platform,
    StyleSheet,
    Text,
    useWindowDimensions,
    View,
} from 'react-native';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';

const NARROW_SYSTEM_MAP_BREAKPOINT = 900;

const OUTPUT_ITEMS = [
    ['App feed', 'mobile-screen-button'],
    ['Email digest', 'envelope-open-text'],
    ['RSS', 'square-rss'],
    ['Agents / MCP, CLI', 'terminal'],
] as const;

function InputsStack() {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={styles.systemMapNodeStack}>
            <View style={[styles.systemMapNode, styles.systemMapNodeConnected]}>
                <View style={styles.systemMapNodeIconShell}>
                    <FontAwesome6 name="inbox" size={14} color={colors.primaryGreenDark} />
                </View>
                <View style={styles.systemMapNodeText}>
                    <Text style={[styles.systemMapNodeTitle, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.forwardedAlert" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                        Forwarded alert
                    </Trans></Text>
                    <Text style={[styles.systemMapNodeBody, fontsLoaded && interRegular]}><Trans id="onboarding.forwardedsystemmap.connected" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                        Connected
                    </Trans></Text>
                </View>
            </View>
            <View style={styles.systemMapNode}>
                <View style={styles.systemMapNodeIconShellMuted}>
                    <FontAwesome6 name="satellite-dish" size={14} color={colors.text500} />
                </View>
                <View style={styles.systemMapNodeText}>
                    <Text style={[styles.systemMapNodeTitle, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.otherSources" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                        Other sources
                    </Trans></Text>
                    <Text style={[styles.systemMapNodeBody, fontsLoaded && interRegular]}><Trans id="onboarding.forwardedsystemmap.arxivBiorxivMedrxivChemrxivPubmed" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                        arXiv, bioRxiv, medRxiv, ChemRxiv, IACR ePrint, PubMed,...
                    </Trans></Text>
                </View>
            </View>
        </View>
    );
}

function FocusCard() {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={styles.systemMapFocusCard}>
            <View style={styles.focusBadge}>
                <Text style={[styles.focusBadgeText, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.current" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Current</Trans></Text>
            </View>
            <Text style={[styles.systemMapFocusTitle, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.researchTopic" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                Research topic
            </Trans></Text>
            <Text style={[styles.systemMapFocusBody, fontsLoaded && interRegular]}><Trans id="onboarding.forwardedsystemmap.thisIsTheFilterThatTellsPaperzillaWhatToKeep" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">
                This is the filter that tells Paperzilla what to keep from every source.
            </Trans></Text>
        </View>
    );
}

function OutputsGrid({ stacked }: { stacked: boolean }) {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={[styles.outputGrid, stacked && styles.outputGridStacked]}>
            {OUTPUT_ITEMS.map(([label, icon]) => (
                <View key={label} style={[styles.outputChip, stacked && styles.outputChipStacked]}>
                    <FontAwesome6 name={icon} size={13} color={colors.text700} />
                    <Text style={[styles.outputChipText, fontsLoaded && interSemiBold]}>{label}</Text>
                </View>
            ))}
        </View>
    );
}

function FlowArrow({ active = false }: { active?: boolean }) {
    return (
        <View style={styles.flowArrowWrap}>
            <View style={[styles.flowArrowBadge, active && styles.flowArrowBadgeActive]}>
                <FontAwesome6
                    name="arrow-down"
                    size={14}
                    color={active ? colors.primaryGreenDark : colors.text500}
                />
            </View>
        </View>
    );
}

function NarrowSystemMap() {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={[styles.systemMapCard, styles.systemMapCardNarrow]}>
            <View style={[styles.systemMapGlow, styles.systemMapGlowNarrow]} />

            <View style={styles.narrowSection}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.inputs" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Inputs</Trans></Text>
                <View style={styles.sectionSurface}>
                    <InputsStack />
                </View>
            </View>

            <FlowArrow active />

            <View style={styles.narrowSection}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.currentStep" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Current step</Trans></Text>
                <FocusCard />
            </View>

            <FlowArrow />

            <View style={styles.narrowSection}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.outputs" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Outputs</Trans></Text>
                <View style={styles.sectionSurface}>
                    <OutputsGrid stacked />
                </View>
            </View>
        </View>
    );
}

function WideSystemMap() {
    const { fontsLoaded } = useFontLoading();

    return (
        <View style={styles.systemMapCard}>
            <View style={styles.systemMapGlow} />
            <View style={styles.systemMapColumn}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.inputs" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Inputs</Trans></Text>
                <InputsStack />
            </View>

            <View style={styles.systemMapRail}>
                <View style={styles.systemMapRailLine} />
                <FontAwesome6 name="arrow-right" size={16} color={colors.primaryGreenDark} />
                <View style={styles.systemMapRailLine} />
            </View>

            <View style={styles.systemMapCenter}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.currentStep" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Current step</Trans></Text>
                <FocusCard />
            </View>

            <View style={styles.systemMapRail}>
                <View style={styles.systemMapRailLine} />
                <FontAwesome6 name="arrow-right" size={16} color={colors.text500} />
                <View style={styles.systemMapRailLine} />
            </View>

            <View style={styles.systemMapColumn}>
                <Text style={[styles.systemMapLabel, fontsLoaded && interSemiBold]}><Trans id="onboarding.forwardedsystemmap.outputs" comment="UI copy in components/onboarding/ForwardedSystemMap.tsx">Outputs</Trans></Text>
                <OutputsGrid stacked={false} />
            </View>
        </View>
    );
}

export default function ForwardedSystemMap() {
    const { width } = useWindowDimensions();
    const isNarrow = width <= NARROW_SYSTEM_MAP_BREAKPOINT;

    if (isNarrow) {
        return <NarrowSystemMap />;
    }

    return <WideSystemMap />;
}

const styles = StyleSheet.create({
    systemMapCard: {
        backgroundColor: '#ffffff',
        borderColor: '#dbe3dc',
        borderRadius: 28,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 18,
        overflow: 'hidden',
        padding: 24,
        position: 'relative',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
        }),
    },
    systemMapCardNarrow: {
        flexDirection: 'column',
        gap: 14,
        padding: 18,
    },
    systemMapGlow: {
        backgroundColor: 'rgba(134, 239, 172, 0.18)',
        borderRadius: 180,
        height: 220,
        position: 'absolute',
        right: -40,
        top: -70,
        width: 220,
    },
    systemMapGlowNarrow: {
        height: 160,
        right: -20,
        top: -26,
        width: 160,
    },
    systemMapColumn: {
        flex: 1,
        gap: 12,
        minWidth: 0,
    },
    narrowSection: {
        gap: 10,
    },
    systemMapLabel: {
        ...fonts.label,
        color: colors.text600,
        textTransform: 'uppercase',
    },
    sectionSurface: {
        backgroundColor: '#f7fbf7',
        borderColor: '#dbe8dc',
        borderRadius: 22,
        borderWidth: 1,
        padding: 14,
    },
    systemMapNodeStack: {
        gap: 12,
    },
    systemMapNode: {
        backgroundColor: '#f8faf8',
        borderColor: '#e5ebe6',
        borderRadius: 20,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 12,
        padding: 14,
    },
    systemMapNodeConnected: {
        backgroundColor: '#f2fbf4',
        borderColor: '#bfe2c8',
    },
    systemMapNodeIconShell: {
        alignItems: 'center',
        backgroundColor: '#dff3e4',
        borderRadius: 14,
        height: 34,
        justifyContent: 'center',
        width: 34,
    },
    systemMapNodeIconShellMuted: {
        alignItems: 'center',
        backgroundColor: '#eef2f4',
        borderRadius: 14,
        height: 34,
        justifyContent: 'center',
        width: 34,
    },
    systemMapNodeText: {
        flex: 1,
        gap: 2,
    },
    systemMapNodeTitle: {
        ...fonts.bodyMedium,
        color: colors.text900,
    },
    systemMapNodeBody: {
        ...fonts.bodySmall,
        color: colors.text600,
    },
    flowArrowWrap: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 2,
    },
    flowArrowBadge: {
        alignItems: 'center',
        backgroundColor: '#eef2f4',
        borderRadius: 999,
        height: 34,
        justifyContent: 'center',
        width: 34,
    },
    flowArrowBadgeActive: {
        backgroundColor: '#e6f6ea',
    },
    systemMapRail: {
        alignItems: 'center',
        gap: 8,
        justifyContent: 'center',
        minWidth: 40,
    },
    systemMapRailLine: {
        backgroundColor: '#d6dae1',
        flex: 1,
        minHeight: 32,
        width: 1,
    },
    systemMapCenter: {
        gap: 12,
        minWidth: 0,
        width: 260,
    },
    systemMapFocusCard: {
        backgroundColor: '#10261b',
        borderRadius: 24,
        gap: 12,
        minHeight: 170,
        padding: 18,
        ...(Platform.OS === 'web' && {
            boxShadow: '0 16px 26px rgba(16, 38, 27, 0.22)',
        }),
    },
    focusBadge: {
        alignSelf: 'flex-start',
        backgroundColor: 'rgba(255,255,255,0.12)',
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    focusBadgeText: {
        ...fonts.label,
        color: '#dcfce7',
    },
    systemMapFocusTitle: {
        ...fonts.h3,
        color: '#ffffff',
    },
    systemMapFocusBody: {
        ...fonts.bodyMedium,
        color: 'rgba(255,255,255,0.82)',
    },
    outputGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    outputGridStacked: {
        flexDirection: 'column',
        flexWrap: 'nowrap',
    },
    outputChip: {
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        borderColor: '#e2e8f0',
        borderRadius: 18,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    outputChipStacked: {
        alignSelf: 'stretch',
    },
    outputChipText: {
        ...fonts.bodySmall,
        color: colors.text700,
    },
});
